/**
 * Does a conversation's composer show the model picker at all?
 *
 * A picker with one entry offers no choice — switching is meaningless — so
 * when the FINAL list for this conversation holds exactly one model the
 * composer renders no picker (operator decision 2026-10-18). "Final" means
 * the list the picker would actually show: `GET /ai/models/` with the agent's
 * allowed families as `allowed_models`, which the server intersects with the
 * families the tenant enabled (super admins see the platform list). Model
 * choice has no per-user override, so that response is the whole answer —
 * never a raw config field.
 *
 * Everything else stays as it was: two or more models show the picker, and
 * so does an empty list (a broken configuration keeps today's icon). Only the
 * DISPLAY changes; the conversation still runs on the one model through the
 * same client selection and the same server-side resolution.
 *
 * Pure and dependency-free so `tests/unit/conversations/model_picker.test.mjs`
 * can run the real code.
 */

/** What a composer receives as its agent's allowed models. */
export type AllowedModelsInput = string | readonly string[] | null | undefined;

/**
 * The agent-scope refs the composer sends to `/ai/models/` — or null for
 * "every family the tenant enabled". Only a non-empty array narrows the
 * list, exactly as the composer's fetch always has ('all', '' or [] do not).
 */
export function agentModelScope(allowedModels: AllowedModelsInput): string[] | null {
  if (Array.isArray(allowedModels) && allowedModels.length > 0) return [...allowedModels];
  return null;
}

/**
 * Whose pins name the families: the conversation's tenant (a project thread
 * may belong to another tenant than the viewer's), else the agent's tenant
 * (the editor). Absent, the server answers for the tenant the request acts
 * in. See backend `chat._catalog_tenant`.
 */
export interface CatalogContext {
  conversationId?: string | null;
  chatbotId?: string | null;
}

/** The `/ai/models/` url for a scope — also the cache key of its answer. */
export function modelCatalogUrl(scope: readonly string[] | null, context?: CatalogContext): string {
  const params: string[] = [];
  if (scope) params.push(`allowed_models=${encodeURIComponent(scope.join(','))}`);
  if (context?.conversationId) params.push(`conversation_id=${encodeURIComponent(context.conversationId)}`);
  else if (context?.chatbotId) params.push(`chatbot_id=${encodeURIComponent(context.chatbotId)}`);
  return params.length ? `/ai/models/?${params.join('&')}` : '/ai/models/';
}

/**
 * Whether an agent of the catalog's organization may run this family: it is
 * switched on there. A super admin's catalog carries the switched-off families
 * too (`enabled: false` - they configure the switches), and the agent save
 * refuses them (`agent_model_config.canonicalize_input`, the same switches) -
 * so a place that picks a model for an agent does not list them at all
 * (Segev, 2026-10-27). Prod 2026-10-27: a super admin ticked "GPT-5.6 Luna"
 * and Save answered 400.
 */
export function isFamilyUsable(family: { enabled?: boolean | null }): boolean {
  return family.enabled !== false;
}

/**
 * @param scope  the agent scope (`agentModelScope`)
 * @param models the final list for that scope, or null while it is not known
 *               yet. Before it arrives, an agent scoped to a single ref can
 *               hold at most one model, so its picker never appears and
 *               vanishes; any wider scope shows the picker at once, as it
 *               always has.
 */
export function showModelPicker(
  scope: readonly string[] | null,
  models: readonly unknown[] | null,
): boolean {
  if (models) return models.length !== 1;
  return !(scope && scope.length === 1);
}
